import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import { authService, supabase } from '../Supabase/supabase';
import { getCartItemCount } from '../../utils/cart';
import OrderNotification from '../OrderNotification/OrderNotification';

const Header = ({ isAuthenticated, onLogout, currentProduct, onProductSelect, showProductsOnHeader = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isShopkeeper, setIsShopkeeper] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isOnAdminPage, setIsOnAdminPage] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);

  // Check if user is admin or shopkeeper
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!isAuthenticated) {
        setIsAdmin(false);
        setIsShopkeeper(false);
        return;
      }

      try {
        const user = await authService.getCurrentUser();
        if (!user) {
          setIsAdmin(false);
          setIsShopkeeper(false);
          return;
        }

        // Check admin status
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('is_admin')
          .eq('email', user.email)
          .single();

        if (userError) throw userError;
        const adminStatus = userData?.is_admin || false;
        setIsAdmin(adminStatus);

        // Check shopkeeper status via RPC (gets user_type from auth metadata)
        if (!adminStatus) {
          try {
            const { data: rpcData, error: rpcError } = await supabase
              .rpc('get_users_with_type');
            
            if (!rpcError && rpcData) {
              const dbUser = rpcData.find(u => u.email === user.email);
              if (dbUser && dbUser.user_type === 'shopkeeper') {
                setIsShopkeeper(true);
              } else {
                setIsShopkeeper(false);
              }
            } else {
              // Fallback: check user_metadata
              const userType = user.user_metadata?.user_type;
              setIsShopkeeper(userType === 'shopkeeper');
            }
          } catch (rpcErr) {
            console.error('Error checking shopkeeper status:', rpcErr);
            // Fallback: check user_metadata
            const userType = user.user_metadata?.user_type;
            setIsShopkeeper(userType === 'shopkeeper');
          }
        } else {
          setIsShopkeeper(false); // Admins are not shopkeepers
        }
      } catch (err) {
        console.error('Error checking user status:', err);
        setIsAdmin(false);
        setIsShopkeeper(false);
      }
    };

    checkUserStatus();
  }, [isAuthenticated]);


  // Check if we're on the admin panel page
  useEffect(() => {
    const checkAdminPage = () => {
      const currentHash = window.location.hash;
      const onAdminPage = currentHash === '#admin';
      setIsOnAdminPage(onAdminPage);
    };

    // Check on mount
    checkAdminPage();

    // Listen for hash changes
    window.addEventListener('hashchange', checkAdminPage);
    
    // Also listen for popstate (back/forward navigation)
    window.addEventListener('popstate', checkAdminPage);

    return () => {
      window.removeEventListener('hashchange', checkAdminPage);
      window.removeEventListener('popstate', checkAdminPage);
    };
  }, []);

  // Load and update cart item count
  useEffect(() => {
    const updateCartCount = () => {
      setCartItemCount(getCartItemCount());
    };

    // Load initial count
    updateCartCount();

    // Listen for cart updates
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);
  // eslint-disable-next-line no-unused-vars
  const handleLogout = async () => {
    try {
      await authService.signOut();
      localStorage.removeItem('cvBuilderAuth');
      localStorage.removeItem('selectedApp');
      if (onLogout) {
        onLogout();
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('cvBuilderAuth');
      localStorage.removeItem('selectedApp');
      window.location.href = '/';
    }
  };

  // eslint-disable-next-line no-unused-vars
  const goToProducts = () => {
    // Navigate to products page without logging out
    // Always preserve authentication state first
    const wasAuthenticated = isAuthenticated || localStorage.getItem('cvBuilderAuth') === 'true';
    
    if (wasAuthenticated) {
      // Preserve authentication state
      localStorage.setItem('cvBuilderAuth', 'true');
    }
    
    // Set flag to show products page
    localStorage.setItem('selectedApp', 'marketplace');
    localStorage.setItem('showProductsPage', 'true');
    sessionStorage.setItem('showProductsPage', 'true');
    
    // Use hash-based navigation instead of page reload to preserve session
    if (window.location.pathname !== '/marketplace') {
      window.location.href = '/marketplace';
      window.dispatchEvent(new CustomEvent('navigateToMarketplace'));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSignIn = () => {
    // Simple: Always show the login form
    // Set flag in both storages to ensure it's available immediately
    localStorage.setItem('showLoginForm', 'true');
    sessionStorage.setItem('showLoginForm', 'true');
    
    // If on homepage, show login form on homepage
    if (location.pathname === '/') {
      // Try to show login form directly if HomePage component is mounted
      if (window.showLoginFormHomepage) {
        window.showLoginFormHomepage();
      } else {
        // Dispatch event to trigger form display
        window.dispatchEvent(new CustomEvent('showLoginFormHomepage'));
      }
    } else if (location.pathname === '/marketplace') {
      // If already on marketplace, show login form immediately
      if (window.showLoginForm) {
        window.showLoginForm();
      } else {
        // Dispatch event to trigger form display
        window.dispatchEvent(new CustomEvent('showLoginForm'));
      }
    } else {
      // Navigate to homepage where login form will be shown
      navigate('/');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const navigateToCVBuilder = () => {
    if (!isAuthenticated) {
      // User is not signed in: Show login form
      sessionStorage.setItem('navigateToCVBuilder', 'true');
      localStorage.setItem('navigateToCVBuilder', 'true');
      localStorage.setItem('selectedApp', 'cv-builder');
      if (showProductsOnHeader && window.showLoginForm) {
        window.showLoginForm();
      } else {
        localStorage.setItem('showLoginForm', 'true');
        sessionStorage.setItem('showLoginForm', 'true');
        navigate('/marketplace');
      }
    } else {
      // User is signed in: Navigate directly to CV Builder dashboard
      // Set navigation flags to prevent logout
      sessionStorage.setItem('isNavigating', 'true');
      sessionStorage.setItem('navigationTimestamp', Date.now().toString());
      localStorage.setItem('selectedApp', 'cv-builder');
      sessionStorage.setItem('navigateToCVBuilder', 'true');
      localStorage.setItem('navigateToCVBuilder', 'true');
      localStorage.removeItem('showProductsPage');
      sessionStorage.removeItem('showProductsPage');
      if (window.resetProductsPageFlag) {
        window.resetProductsPageFlag();
      }
      // Navigate to /cv-builder route using React Router
      navigate('/cv-builder');
    }
  };

  const navigateToHomePage = () => {
    // Preserve authentication state first
    const wasAuthenticated = isAuthenticated || localStorage.getItem('cvBuilderAuth') === 'true';
    
    if (wasAuthenticated) {
      // Preserve authentication state
      localStorage.setItem('cvBuilderAuth', 'true');
    }
    
    // Set navigation flag to prevent logout
    sessionStorage.setItem('isNavigating', 'true');
    sessionStorage.setItem('navigationTimestamp', Date.now().toString());
    
    // Set a flag to indicate homepage navigation intent
    sessionStorage.setItem('navigateToHomePage', 'true');
    
    // Clear navigation flags but preserve auth
    localStorage.removeItem('selectedApp');
    localStorage.removeItem('showProductsPage');
    localStorage.removeItem('navigateToCVBuilder');
    localStorage.removeItem('navigateToIDCardPrint');
    sessionStorage.removeItem('showProductsPage');
    sessionStorage.removeItem('navigateToCVBuilder');
    sessionStorage.removeItem('navigateToIDCardPrint');
    
    // Navigate to homepage without reload - use hash and custom event
    window.location.hash = '';
    window.history.replaceState(null, '', '/');
    // Trigger navigation event for App.js to handle
    window.dispatchEvent(new CustomEvent('navigateToHomePage'));
  };

  // eslint-disable-next-line no-unused-vars
  const navigateToIDCardDashboard = () => {
    if (!isAuthenticated) {
      // User is not signed in: Show login form
      sessionStorage.setItem('navigateToIDCardPrint', 'true');
      localStorage.setItem('navigateToIDCardPrint', 'true');
      localStorage.setItem('selectedApp', 'id-card-print');
      localStorage.setItem('idCardView', 'dashboard');
      if (showProductsOnHeader && window.showLoginForm) {
        window.showLoginForm();
      } else {
        localStorage.setItem('showLoginForm', 'true');
        sessionStorage.setItem('showLoginForm', 'true');
        navigate('/');
      }
    } else {
      // User is signed in: Navigate directly to ID Card Dashboard
      // Set navigation flags to prevent logout
      sessionStorage.setItem('isNavigating', 'true');
      sessionStorage.setItem('navigationTimestamp', Date.now().toString());
      sessionStorage.setItem('navigateToIDCardPrint', 'true');
      localStorage.setItem('navigateToIDCardPrint', 'true');
      localStorage.setItem('selectedApp', 'id-card-print');
      localStorage.setItem('idCardView', 'dashboard');
      // Clear any marketplace flags
      localStorage.removeItem('showProductsPage');
      sessionStorage.removeItem('showProductsPage');
      // Navigate to /id-card-print route using React Router
      navigate('/id-card-print');
    }
  };

  // Handle navigation to sections on products page
  // eslint-disable-next-line no-unused-vars
  const scrollToSection = (sectionId) => {
    setTimeout(() => {
    const element = document.getElementById(sectionId);
    if (element) {
        // Find the scrollable products-page container
        const productsPage = document.querySelector('.products-page');
        if (productsPage) {
          // Get the header height for offset calculation
          // eslint-disable-next-line no-unused-vars
          const header = document.querySelector('.app-header');
          const headerOffset = 20; // Extra spacing from top of container
          
          // Get current scroll position of the container
          const currentScrollTop = productsPage.scrollTop;
          
          // Get bounding rectangles
          const containerRect = productsPage.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          
          // Calculate element's position relative to the container's scrollable content
          // elementRect.top is relative to viewport
          // containerRect.top is container's top relative to viewport
          // We need: element position in container's content = current scroll + (element top - container top)
          const elementTopInContainer = currentScrollTop + (elementRect.top - containerRect.top);
          
          // Calculate final scroll position with header offset
          const offsetPosition = elementTopInContainer - headerOffset;
          
          // Scroll the products-page container
          productsPage.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        } else {
          // Fallback: if products-page not found, try window scroll (for compatibility)
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + (window.pageYOffset || window.scrollY || 0) - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
      } else {
        console.warn(`Section with id "${sectionId}" not found. Please ensure you are on the products page.`);
      }
    }, 100);
  };



  return (
    <>
      <header className="app-header">
        <div className="header-container">
          <div className="header-left">
            <img 
              src="/images/glory-logo.png" 
              alt="Glory Logo" 
              className="glory-logo"
              onClick={navigateToHomePage}
              style={{ cursor: 'pointer' }}
              title="Go to Homepage"
              onError={(e) => {
                // Fallback if logo doesn't exist - hide it
                e.target.style.display = 'none';
              }}
            />
          </div>

          <div className="header-center">
            {/* Navigation buttons removed */}
          </div>

          <div className="header-right">
            {/* Cart Button - Show on products page */}
            {showProductsOnHeader && (
              <button
                onClick={() => window.location.href = '/cart'}
                className="cart-btn-header"
                title="View Cart"
              >
Cart
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </button>
            )}
            
            {isAuthenticated && isAdmin && location.pathname !== '/admin' && !location.pathname.startsWith('/admin/') && (
              <button 
                type="button"
                onClick={() => {
                  // Navigate to admin dashboard using clean URL
                  navigate('/admin');
                }}
                className="admin-btn-header"
                title="Open Admin Dashboard"
              >
                Admin Panel
              </button>
            )}
            
            {isAuthenticated && isShopkeeper && !isAdmin && location.pathname !== '/shopkeeper' && !location.pathname.startsWith('/shopkeeper/') && (
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Clear any navigation flags that might interfere
                  sessionStorage.removeItem('navigateToCVBuilder');
                  sessionStorage.removeItem('navigateToIDCardPrint');
                  localStorage.removeItem('navigateToCVBuilder');
                  localStorage.removeItem('navigateToIDCardPrint');
                  // Clear any app selection that might interfere
                  localStorage.removeItem('selectedApp');
                  // Navigate to shopkeeper dashboard using direct URL (like cart button)
                  console.log('Navigating to shopkeeper panel...');
                  window.location.href = '/shopkeeper';
                }}
                className="shopkeeper-btn-header"
                title="Open Shopkeeper Panel"
              >
                Shopkeeper Panel
              </button>
            )}
            
            {isAuthenticated && location.pathname !== '/profile' && (
              <button 
                type="button"
                onClick={() => navigate('/profile')}
                className="profile-btn-header"
                title="View My Profile"
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}
              >
                Profile
              </button>
            )}
          </div>
        </div>
      </header>
      {/* Order Notification - Always visible for admins */}
      <OrderNotification isAdmin={isAdmin} isAuthenticated={isAuthenticated} />
    </>
  );
};

export default Header;

